import * as React from 'react';

// Unlayer emits design:updated on every keystroke and the newsletter viewmodel silently drops a
// save that overlaps one already in flight, so bursts have to be collapsed before saving. Matches
// the debounce and retry budget AutomationFlows' useAutoSave uses.
export const AutoSaveDebounceMs = 500;
export const AutoSaveMaxRetries = 3;

interface IOptions<T> {
	/** Performs the save. Resolves the saved record, or a falsy value when the save did not land. */
	onAutoSave: () => Promise<T | null | undefined>;
	/** Whether the document still has changes worth retrying for. */
	isDirty: () => boolean;
}

/**
 * Debounces autosave requests and retries the ones that never reached the server.
 *
 * A save can resolve without persisting anything — the newsletter viewmodel guards `save()` behind
 * a `busy` flag and returns `undefined` when another save is in flight — so a falsy result is
 * treated as "did not land" and re-armed, up to `AutoSaveMaxRetries` consecutive attempts. Fresh
 * editor activity resets that budget; only consecutive drops exhaust it.
 */
export function useDebouncedAutoSave<T>({ onAutoSave, isDirty }: IOptions<T>) {
	const onAutoSaveRef = React.useRef(onAutoSave);
	const isDirtyRef = React.useRef(isDirty);
	React.useEffect(() => {
		onAutoSaveRef.current = onAutoSave;
		isDirtyRef.current = isDirty;
	}, [onAutoSave, isDirty]);

	const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const runningRef = React.useRef(false);
	const retriesRef = React.useRef(0);
	const armRef = React.useRef<() => void>(() => undefined);

	const reArm = React.useCallback(() => {
		if (retriesRef.current >= AutoSaveMaxRetries) {
			return;
		}
		retriesRef.current += 1;
		armRef.current();
	}, []);

	const arm = React.useCallback(() => {
		if (timerRef.current) {
			clearTimeout(timerRef.current);
		}
		timerRef.current = setTimeout(async () => {
			timerRef.current = null;
			if (runningRef.current) {
				reArm();
				return;
			}
			runningRef.current = true;
			try {
				const saved = await onAutoSaveRef.current();
				if (saved) {
					retriesRef.current = 0;
					return;
				}
				if (isDirtyRef.current()) {
					reArm();
				}
			} finally {
				runningRef.current = false;
			}
		}, AutoSaveDebounceMs);
	}, [reArm]);

	React.useEffect(() => {
		armRef.current = arm;
	}, [arm]);

	React.useEffect(() => {
		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
			}
		};
	}, []);

	return React.useCallback(() => {
		retriesRef.current = 0;
		arm();
	}, [arm]);
}
